import React, { useState, useEffect, useMemo } from "react";
import { FaCheckCircle, FaSun, FaMoon } from "react-icons/fa";
import { FiEdit, FiTrash2, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { FaPlus, FaMinus } from "react-icons/fa";
import SummaryApi from "../common";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import AdditionalInformationStep from "./AdditionalInformationStep";
import CurriculumStep from "./CurriculumStep";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";

const steps = [
  "Course Information",
  "Course Media",
  "Curriculum",
  "Additional Information",
  "Pricing",
];

const CreateCourse = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [courseId, setCourseId] = useState(null);
  const [isFree, setIsFree] = useState(true);
  const [hasDiscount, setHasDiscount] = useState(false);
  const [expiryType, setExpiryType] = useState("lifetime");
  const [courseSubtitle, setCourseSubtitle] = useState("");
  const [isTrendy, setIsTrendy] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // === ONLY CHANGE: Theme follows localStorage toggle, NOT system preference ===
  useEffect(() => {
    const savedTheme = sessionStorage.getItem("admin-theme");
    setIsDarkMode(savedTheme === "dark");
  }, []);

  useEffect(() => {
    const handleThemeChange = () => {
      const savedTheme = sessionStorage.getItem("admin-theme");
      setIsDarkMode(savedTheme === "dark");
    };

    // Listen for changes in localStorage (triggered by AdminPanel toggle)
    window.addEventListener("storage", handleThemeChange);
    return () => window.removeEventListener("storage", handleThemeChange);
  }, []);
  // =======================================================================

  // === Course Info States ===
  const [courseData, setCourseData] = useState({});
  const [courseTitle, setCourseTitle] = useState("");
  const [courseCategory, setCourseCategory] = useState("");
  const [courseDuration, setCourseDuration] = useState("");
  const [courseLevel, setCourseLevel] = useState("");
  const [courseLanguage, setCourseLanguage] = useState("");
  const [maxStudents, setMaxStudents] = useState("");
  const [courserating, setCourseRating] = useState("");
  const [courseVisibility, setCourseVisibility] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [courseDescription, setCourseDescription] = useState("");
  const [isCourseCreatedInitially, setIsCourseCreatedInitially] = useState(false);
  const [studentLearning, setStudentLearning] = useState("");
  const [requirements, setRequirements] = useState("");
  const [isCareer, setIsCareer] = useState(false);
  const [isDegreeCourse, setIsDegreeCourse] = useState(false); // New state for is_degree_course

  const [thumbanail, setThumbnail] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [courseImage1, setCourseImage1] = useState(null);
  const [previewUrlImage1, setPreviewUrlImage1] = useState(null);
  const [courseImage2, setCourseImage2] = useState(null);
  const [previewUrlImage2, setPreviewUrlImage2] = useState(null);
  const [courseVideoLink, setCourseVideoLink] = useState("");
  const [coursePrice, setCoursePrice] = useState("");
  const [discountPercentage, setDiscountPercentage] = useState("");

  const [categories, setCategories] = useState([]);

  const [showTopicModal, setShowTopicModal] = useState(false);
  const [newTopicData, setNewTopicData] = useState({
    topic_name: "",
    duration: "",
    day_type: "",
  });
  const [topics, setTopics] = useState([]);

  const totalPrice = useMemo(() => {
    const price = parseFloat(coursePrice);
    const discount = parseFloat(discountPercentage);

    if (isNaN(price) || price <= 0 || isFree) {
      return 0;
    }

    if (hasDiscount && !isNaN(discount) && discount > 0) {
      const discountedAmount = price * (discount / 100);
      return (price - discountedAmount).toFixed(2);
    }
    return price.toFixed(2);
  }, [coursePrice, discountPercentage, isFree, hasDiscount]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(SummaryApi.getActiveCategories.url);
        const json = await res.json();

        if (res.ok && json.status) {
          setCategories(json.data);
        } else {
          toast.error("Failed to fetch categories");
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        toast.error("Error fetching categories");
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const idFromUrl = params.get("id");

    if (idFromUrl) {
      setCourseId(idFromUrl);
      setIsCourseCreatedInitially(true);
      const loadCourseData = async () => {
        const data = await fetchCourseDetails(idFromUrl);
        if (data) {
          setCourseTitle(data.course_title || "");
          setCourseSubtitle(data.subtitle || "");
          setCourseCategory(data.category_id || "");
          setCourseDuration(data.course_duration || "");
          setCourseLevel(data.diffculty_level || "");
          setCourseLanguage(data.language_instruction || "");
          setMaxStudents(data.max_students || "");
          setCourseRating(data.rating || "");
          setCourseVisibility(data.course_type || "");
          setShortDescription(data.short_desc || "");
          setCourseDescription(data.description || "");
          setStudentLearning(data.student_learning || "");
          setRequirements(data.learning_requirements || "");
          setIsCareer(data.is_career_course === 1);
          setIsTrendy(data.is_trendy_course === 1);
          setIsDegreeCourse(data.is_degree_course === 1); // Set new state
          setIsActive(data.is_active === 1);
          setIsFree(data.is_free === 1);
          setCoursePrice(data.course_price || "");
          
          if (data.discount && parseFloat(data.discount) > 0) {
            setHasDiscount(true);
            setDiscountPercentage(data.discount);
          } else {
            setHasDiscount(false);
            setDiscountPercentage("");
          }

          setCourseVideoLink(data.course_video_link || "");

          if (data.course_thumbnail) {
            let fullThumbnailUrl = SummaryApi.Images.url + data.course_thumbnail;
            fullThumbnailUrl = fullThumbnailUrl.replace("/apiuploads/", "/uploads/");
            setPreviewUrl(fullThumbnailUrl);
          }

          if (data.course_image1) {
            let fullImage1Url = SummaryApi.Images.url + data.course_image1;
            fullImage1Url = fullImage1Url.replace("/apiuploads/", "/uploads/");
            setPreviewUrlImage1(fullImage1Url);
          }

          if (data.course_image2) {
            let fullImage2Url = SummaryApi.Images.url + data.course_image2;
            fullImage2Url = fullImage2Url.replace("/apiuploads/", "/uploads/");
            setPreviewUrlImage2(fullImage2Url);
          }
        }
      };
      loadCourseData();
    }
  }, [location.search]);

  useEffect(() => {
    if (thumbanail instanceof File) {
      const objectUrl = URL.createObjectURL(thumbanail);
      setPreviewUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else if (!thumbanail && !courseId) {
      setPreviewUrl(null);
    }
  }, [thumbanail, courseId]);

  useEffect(() => {
    if (courseImage1 instanceof File) {
      const objectUrl = URL.createObjectURL(courseImage1);
      setPreviewUrlImage1(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else if (!courseImage1 && !courseId) {
      setPreviewUrlImage1(null);
    }
  }, [courseImage1, courseId]);

  useEffect(() => {
    if (courseImage2 instanceof File) {
      const objectUrl = URL.createObjectURL(courseImage2);
      setPreviewUrlImage2(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else if (!courseImage2 && !courseId) {
      setPreviewUrlImage2(null);
    }
  }, [courseImage2, courseId]);

  const handleTrendyToggle = () => {
    const newIsTrendy = !isTrendy;
    setIsTrendy(newIsTrendy);
    if (newIsTrendy) {
      setCourseDuration("12 weeks");
    } else {
      setCourseDuration("");
    }
  };

  const handleNext = async () => {
    let validationPassed = true;

    if (currentStep === 0) {
      if (
        !courseTitle ||
        !courseCategory ||
        !courseLevel ||
        !courseLanguage ||
        !maxStudents ||
        !shortDescription ||
        !courseDescription
      ) {
        toast.error("Please fill in all required fields in Course Information.");
        validationPassed = false;
      }

      if (validationPassed) {
        let newlyCreatedCourseId = courseId;

        if (!isCourseCreatedInitially || !courseId) {
          newlyCreatedCourseId = await handleCreateCourse();

          if (!newlyCreatedCourseId) {
            validationPassed = false;
          } else {
            setIsCourseCreatedInitially(true);
            setCourseId(newlyCreatedCourseId);
            toast.success(`${steps[currentStep]} completed!`);
          }
        } else {
          const updateSuccess = await handleSubmit(false);
          if (updateSuccess) {
            toast.success(`${steps[currentStep]} completed!`);
          } else {
            validationPassed = false;
          }
        }
      }
    } 
    else if (currentStep === 1) {
      if (!thumbanail && !previewUrl) {
        toast.error("Please upload a Course Thumbnail.");
        validationPassed = false;
      }
      if (!courseId && validationPassed) {
        toast.error("Please create the course first (Step 1).");
        validationPassed = false;
      }

      if (validationPassed) {
        const updateSuccess = await handleSubmit(false);
        if (updateSuccess) {
          toast.success(`${steps[currentStep]} completed!`);
        } else {
          validationPassed = false;
        }
      }
    } 
    else if (currentStep === 4) {
      if (!isFree && validationPassed) {
        if (!coursePrice || parseFloat(coursePrice) <= 0) {
          toast.error("Please enter a valid Course Price for paid courses.");
          validationPassed = false;
        }
        if (hasDiscount && (!discountPercentage || parseFloat(discountPercentage) <= 0)) {
          toast.error("Please enter a valid Discount Percentage if discount is enabled.");
          validationPassed = false;
        }
        if (hasDiscount && parseFloat(discountPercentage) >= 100 && validationPassed) {
          toast.error("Discount percentage cannot be 100% or more.");
          validationPassed = false;
        }
      }
      if (!courseId && validationPassed) {
        toast.error("Please create the course first (Step 1).");
        validationPassed = false;
      }

      if (validationPassed) {
        const updateSuccess = await handleSubmit(false);
        if (updateSuccess) {
          toast.success(`${steps[currentStep]} completed!`);
        } else {
          validationPassed = false;
        }
      }
    }

    if (validationPassed) {
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps([...completedSteps, currentStep]);
      }

      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canAccessStep = (index) => {
    return index === 0 || completedSteps.includes(index - 1) || courseId;
  };

  const handleCreateCourse = async () => {
  const payload = {
    course_title: courseTitle,
    subtitle: courseSubtitle || "N/A",
    category_id: Number(courseCategory),
    diffculty_level: courseLevel,
    language_instruction: courseLanguage,
    max_students: Number(maxStudents),
    course_type: courseVisibility ? courseVisibility.toLowerCase() : "public",
    short_desc: shortDescription,
    description: courseDescription,
    student_learning: studentLearning || "N/A",
    learning_requirements: requirements || "N/A",
    is_career_course: isCareer ? 1 : 0,
    is_trendy_course: isTrendy ? 1 : 0,
    is_degree_course: isDegreeCourse ? 1 : 0, // Add new field
    course_duration: courseDuration,  // Fixed: removed duplicate "course"
    rating: courserating || 0,
    is_active: isActive ? 1 : 0,
  };

    try {
      const res = await fetch(SummaryApi.Coursecreate.url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok && data.status && data.course_id) {
        setCourseId(data.course_id);
        // toast.success("Course created successfully!");
        return data.course_id;
      } else {
        toast.error("Failed to create course. " + (data.message || "Unknown error occurred."));
        return null;
      }
    } catch (error) {
      console.error("Course creation error:", error);
      toast.error("An error occurred while creating the course.");
      return null;
    }
  };

  const fetchCourseDetails = async (id) => {
    try {
      const res = await fetch(SummaryApi.Coursebyid.url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ course_id: id }),
      });

      const data = await res.json();

      if (res.ok && data.status) {
        return data.data;
      } else {
        toast.error("Failed to fetch course details: " + (data.message || ""));
        return null;
      }
    } catch (err) {
      toast.error("An error occurred while fetching course details.");
      return null;
    }
  };

  const handleSubmit = async (showToast = true) => {
    if (!courseId) {
      toast.error("Missing course ID.");
      return false;
    }

    try {
      const existingCourse = await fetchCourseDetails(courseId);
      if (!existingCourse) {
        toast.error("Unable to fetch existing course data.");
        return false;
      }

      const formData = new FormData();
      formData.append("course_id", courseId);
      formData.append("course_title", courseTitle || existingCourse.course_title || "N/A");
      formData.append("category_id", Number(courseCategory) || existingCourse.category_id || 0);
      formData.append("subtitle", courseSubtitle || existingCourse.subtitle || "N/A");
      formData.append("diffculty_level", courseLevel || existingCourse.diffculty_level || "N/A");
      formData.append("language_instruction", courseLanguage || existingCourse.language_instruction || "N/A");
      formData.append("max_students", Number(maxStudents) || existingCourse.max_students || 0);
      formData.append("course_type", (courseVisibility ? courseVisibility.toLowerCase() : "") || existingCourse.course_type || "public");
      formData.append("short_desc", shortDescription || existingCourse.short_desc || "N/A");
      formData.append("description", courseDescription || existingCourse.description || "N/A");
      formData.append("student_learning", studentLearning || existingCourse.student_learning || "N/A");
      formData.append("learning_requirements", requirements || existingCourse.learning_requirements || "N/A");
      formData.append("rating", courserating || existingCourse.rating || "0");
      formData.append("is_free", isFree ? 1 : 0);
      formData.append("is_paid", isFree ? 0 : 1);
      formData.append("expiry_period", expiryType || existingCourse.expiry_period || "lifetime");
      formData.append("course_video_link", courseVideoLink || existingCourse.course_video_link || "N/A");
      formData.append("is_career_course", isCareer ? 1 : 0);
      formData.append("is_trendy_course", isTrendy ? 1 : 0);
      formData.append("is_degree_course", isDegreeCourse ? 1 : 0); // Append new field to formData
      formData.append("course_duration", courseDuration || existingCourse.course_duration || "N/A");
      formData.append("is_active", isActive ? 1 : 0);

      if (!isFree) {
        formData.append("course_price", Number(coursePrice));
        formData.append("discount", hasDiscount ? Number(discountPercentage) : 0);
        formData.append("total_price", parseFloat(totalPrice));
      } else {
        formData.append("course_price", 0);
        formData.append("discount", 0);
        formData.append("total_price", 0);
      }

      if (thumbanail instanceof File) formData.append("course_thumbnail", thumbanail);
      if (courseImage1 instanceof File) formData.append("course_image1", courseImage1);
      if (courseImage2 instanceof File) formData.append("course_image2", courseImage2);

      const res = await fetch(SummaryApi.Courseupdate.url, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (res.ok && data.status) {
        if (showToast) {
          toast.success("Course updated successfully!");
          if (currentStep === steps.length - 1) {
            navigate("/admin-panel/course-list");
          }
        }
        return true;
      } else {
        toast.error("Failed to update course. " + (data.message || ""));
        return false;
      }
    } catch (error) {
      console.error("Update error:", error);
      toast.error("An error occurred while submitting the course.");
      return false;
    }
  };

  // Curriculum fetch remains unchanged
  useEffect(() => {
    const fetchCurriculum = async () => {
      if (!courseId) {
        setTopics([]);
        return;
      }
      try {
        const response = await fetch(SummaryApi.getCurriculumByCourseId.url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ course_id: courseId }),
        });
        const data = await response.json();
        if (data.status) {
          const fetchedTopics = data.data.map((item) => ({
            id: item.curriculum_id,
            topic_name: item.topic_name,
            duration: item.duration,
            day_type: item.day_type,
            course_id: item.course_id,
            is_active: item.is_active,
            isOpen: false,
            lessons: [],
          }));
          setTopics(fetchedTopics);
        } else {
          setTopics([]);
        }
      } catch (error) {
        setTopics([]);
      }
    };
    fetchCurriculum();
  }, [courseId]);

  const toggleTopic = async (id) => {
    setTopics((prev) =>
      prev.map((topic) =>
        topic.id === id ? { ...topic, isOpen: !topic.isOpen } : topic
      )
    );

    const topic = topics.find((t) => t.id === id);
    if (topic && topic.lessons.length === 0) {
      const lessons = await fetchLessonsForTopic(id);
      setTopics((prev) =>
        prev.map((t) => (t.id === id ? { ...t, lessons } : t))
      );
    }
  };

  const handleSaveTopic = async () => {
    if (!courseId) {
      toast.error("Please create the course first (Step 1).");
      return;
    }
    if (newTopicData.topic_name.trim() === "") {
      toast.error("Topic name cannot be empty.");
      return;
    }

    const payload = {
      course_id: courseId,
      topic_name: newTopicData.topic_name,
      duration: newTopicData.duration,
      day_type: newTopicData.day_type,
      is_active: true,
    };

    try {
      const res = await fetch(SummaryApi.createCurriculum.url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok && data.status) {
        toast.success("Topic created successfully!");
        const newTopicObject = {
          id: data.curriculum_id,
          topic_name: newTopicData.topic_name,
          duration: newTopicData.duration,
          day_type: newTopicData.day_type,
          course_id: courseId,
          is_active: 1,
          isOpen: true,
          lessons: [],
        };
        setTopics((prevTopics) => [...prevTopics, newTopicObject]);

        setNewTopicData({ topic_name: "", duration: "", day_type: "" });
        setShowTopicModal(false);
      } else {
        toast.error("Failed to create topic: " + (data.message || "Unknown error"));
      }
    } catch (error) {
      toast.error("An error occurred while creating the topic.");
    }
  };

  const fetchLessonsForTopic = async (curriculumId) => {
    try {
      const response = await fetch(SummaryApi.getCourseLessonByCurriculumId.url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ curriculum_id: curriculumId }),
      });
      const data = await response.json();
      return data.status ? data.data : [];
    } catch (error) {
      return [];
    }
  };

  // Add Topic Modal Component
  const TopicModal = ({ show, onClose, onSave, topicData, onChange, isDarkMode }) => {
    if (!show) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className={`rounded-lg w-full max-w-md transition-colors duration-300 ${
          isDarkMode ? 'bg-gray-800' : 'bg-white'
        }`}>
          <div className={`p-6 border-b transition-colors duration-300 ${
            isDarkMode ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <h3 className={`text-lg font-semibold transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}>Add New Topic</h3>
          </div>
          
          <div className="p-6 space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>Topic Name *</label>
              <input
                type="text"
                value={topicData.topic_name}
                onChange={(e) => onChange({...topicData, topic_name: e.target.value})}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'border-gray-300'
                }`}
                placeholder="Enter topic name"
              />
            </div>
            
            <div>
              <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>Duration</label>
              <input
                type="text"
                value={topicData.duration}
                onChange={(e) => onChange({...topicData, duration: e.target.value})}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'border-gray-300'
                }`}
                placeholder="e.g., 2 hours"
              />
            </div>
            
            <div>
              <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>Day Type</label>
              <input
                type="text"
                value={topicData.day_type}
                onChange={(e) => onChange({...topicData, day_type: e.target.value})}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'border-gray-300'
                }`}
                placeholder="e.g., Day 1"
              />
            </div>
          </div>
          
          <div className={`p-6 border-t flex justify-end space-x-3 transition-colors duration-300 ${
            isDarkMode ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <button
              onClick={onClose}
              className={`px-4 py-2 border rounded-md transition-colors duration-200 ${
                isDarkMode 
                  ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                  : 'border-gray-300 text-gray-700 hover:bg-gray-100'
              }`}
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200"
            >
              Save Topic
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen p-6 transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-indigo-50 to-blue-50'
    }`}>
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className={`text-3xl font-bold transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-800'
          }`}>Add New Course</h1>
        </div>

        {/* Step Navigation */}
        <div className="flex justify-between mb-6 flex-wrap gap-4">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`flex items-center space-x-2 cursor-pointer transition-colors duration-300 ${
                currentStep === index
                  ? "text-blue-600"
                  : canAccessStep(index)
                  ? isDarkMode ? "text-gray-300" : "text-gray-800"
                  : "text-gray-400 cursor-not-allowed"
              }`}
              onClick={() => canAccessStep(index) && setCurrentStep(index)}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                  currentStep === index
                    ? "border-blue-900 bg-blue-100"
                    : completedSteps.includes(index)
                    ? "border-green-600 bg-green-100"
                    : isDarkMode
                    ? "border-gray-600 bg-gray-700"
                    : "border-gray-300 bg-white"
                }`}
              >
                {completedSteps.includes(index) ? (
                  <FaCheckCircle className="text-green-500" />
                ) : (
                  index + 1
                )}
              </div>
              <span className="text-sm font-medium">{step}</span>
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className={`border rounded-lg p-6 shadow transition-colors duration-300 ${
          isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'
        }`}>
          {currentStep === 0 && (
            <div>
              <h2 className={`text-xl font-semibold mb-4 transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-800'
              }`}>Basic Information</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <input
                  type="text"
                  value={courseTitle}
                  onChange={(e) => setCourseTitle(e.target.value)}
                  placeholder="Course Title *"
                  className={`w-full px-4 py-3 border rounded transition-all duration-200 ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500' 
                      : 'border-gray-300 focus:border-blue-600 focus:ring-blue-600'
                  }`}
                />
                <input
                  type="text"
                  value={courseSubtitle}
                  onChange={(e) => setCourseSubtitle(e.target.value)}
                  placeholder="Course Subtitle"
                  className={`w-full px-4 py-3 border rounded transition-all duration-200 ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'border-gray-300'
                  }`}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <select
                  className={`w-full px-4 py-3 border rounded transition-all duration-200 ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'border-gray-300'
                  }`}
                  value={courseCategory}
                  onChange={(e) => setCourseCategory(e.target.value)}
                >
                  <option value="">Select Course Category *</option>
                  {categories.map((cat) => (
                    <option key={cat.category_id} value={cat.category_id}>
                      {cat.category_name}
                    </option>
                  ))}
                </select>

                <select
                  className={`w-full px-4 py-3 border rounded transition-all duration-200 ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'border-gray-300'
                  }`}
                  value={courseLevel}
                  onChange={(e) => setCourseLevel(e.target.value)}
                >
                  <option value="">Select Course Level *</option>
                  <option value="Begginer">Begginer</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <input
                  type="number"
                  value={maxStudents}
                  onChange={(e) => setMaxStudents(e.target.value)}
                  placeholder="Max Number of Students *"
                  className={`w-full px-4 py-3 border rounded transition-all duration-200 ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'border-gray-300'
                  }`}
                />
                <select
                  className={`w-full px-4 py-3 border rounded transition-all duration-200 ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'border-gray-300'
                  }`}
                  value={courseLanguage}
                  onChange={(e) => setCourseLanguage(e.target.value)}
                >
                  <option value="">Select Language *</option>
                  <option value="English">English</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <input
                  type="text"
                  value={courseDuration}
                  onChange={(e) => setCourseDuration(e.target.value)}
                  placeholder="Course Duration (e.g., 6 weeks) *"
                  disabled={isTrendy}
                  className={`w-full px-4 py-3 border rounded transition-all duration-200 ${
                    isTrendy ? 'opacity-60 cursor-not-allowed' : ''
                  } ${isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'border-gray-300'
                  }`}
                />
                <input
                  type="text"
                  value={courserating}
                  onChange={(e) => setCourseRating(e.target.value)}
                  placeholder="Course Rating (e.g., 4.5)"
                  className={`w-full px-4 py-3 border rounded transition-all duration-200 ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'border-gray-300'
                  }`}
                />
              </div>

              <input
                type="text"
                value={shortDescription}
                onChange={(e) => setShortDescription(e.target.value)}
                placeholder="Short Description *"
                className={`w-full mb-3 px-4 py-3 border rounded transition-all duration-200 ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'border-gray-300'
                }`}
              />

              <div className="mb-6">
                <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>Course Description *</label>
                <textarea
                  rows={5}
                  value={courseDescription}
                  onChange={(e) => setCourseDescription(e.target.value)}
                  className={`w-full px-4 py-3 border rounded transition-all duration-200 ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'border-gray-300'
                  }`}
                  placeholder="Write a detailed course description..."
                />
              </div>

              <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>What will students learn?</label>
                  <ReactQuill
                    theme="snow"
                    value={studentLearning}
                    onChange={setStudentLearning}
                    placeholder="E.g., Become a UX designer..."
                    className={`h-40 mb-10 ${isDarkMode ? 'quill-dark' : ''}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Requirements</label>
                  <ReactQuill
                    theme="snow"
                    value={requirements}
                    onChange={setRequirements}
                    placeholder="E.g., No prior experience needed"
                    className={`h-40 mb-10 ${isDarkMode ? 'quill-dark' : ''}`}
                  />
                </div>
              </div>

              <div className="flex items-center space-x-6 mt-4 flex-wrap gap-y-4">
                <div className="flex items-center space-x-3">
                  <label htmlFor="career" className={`text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Career Course</label>
                  <button
                    id="career"
                    onClick={() => setIsCareer(!isCareer)}
                    className={`w-11 h-6 flex items-center rounded-full p-1 cursor-pointer transition-colors duration-300 ${
                      isCareer ? "bg-green-600" : "bg-red-500"
                    }`}
                  >
                    <div className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out ${
                      isCareer ? "translate-x-5" : "translate-x-0"
                    }`} />
                  </button>
                </div>

                <div className="flex items-center space-x-3">
                  <label htmlFor="trendy" className={`text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Trendy Course</label>
                  <button
                    id="trendy"
                    onClick={handleTrendyToggle}
                    className={`w-11 h-6 flex items-center rounded-full p-1 cursor-pointer transition-colors duration-300 ${
                      isTrendy ? "bg-green-600" : "bg-red-500"
                    }`}
                  >
                    <div className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out ${
                      isTrendy ? "translate-x-5" : "translate-x-0"
                    }`} />
                  </button>
                </div>

                <div className="flex items-center space-x-3">
                  <label htmlFor="degree" className={`text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Degree Course</label>
                  <button
                    id="degree"
                    onClick={() => setIsDegreeCourse(!isDegreeCourse)}
                    className={`w-11 h-6 flex items-center rounded-full p-1 cursor-pointer transition-colors duration-300 ${
                      isDegreeCourse ? "bg-green-600" : "bg-red-500"
                    }`}
                  >
                    <div className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out ${
                      isDegreeCourse ? "translate-x-5" : "translate-x-0"
                    }`} />
                  </button>
                </div>

                <div className="flex items-center space-x-3">
                  <label htmlFor="active" className={`text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Course Active</label>
                  <button
                    id="active"
                    onClick={() => setIsActive(!isActive)}
                    className={`w-11 h-6 flex items-center rounded-full p-1 cursor-pointer transition-colors duration-300 ${
                      isActive ? "bg-green-600" : "bg-red-500"
                    }`}
                  >
                    <div className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out ${
                      isActive ? "translate-x-5" : "translate-x-0"
                    }`} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div>
              <h2 className={`text-xl font-semibold mb-4 transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-800'
              }`}>Course Media</h2>
              <p className={`text-sm mb-4 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Intro Course overview provider type. (.mp4, YouTube, Vimeo etc.)
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Course Image 1</label>
                  <input
                    type="file"
                    accept="image/*"
                    className={`border px-4 py-3 rounded w-full transition-all duration-200 ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'border-gray-300'
                    }`}
                    onChange={(e) => setCourseImage1(e.target.files[0])}
                  />
                  <p className={`text-xs mt-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-500'
                  }`}>JPEG, PNG, GIF, and WebP formats, up to 2 MB</p>
                  {previewUrlImage1 && (
                    <div className="mt-4">
                      <img src={previewUrlImage1} alt="Preview 1" className="w-full h-64 object-cover rounded" />
                    </div>
                  )}
                </div>

                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Course Image 2</label>
                  <input
                    type="file"
                    accept="image/*"
                    className={`border px-4 py-3 rounded w-full transition-all duration-200 ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'border-gray-300'
                    }`}
                    onChange={(e) => setCourseImage2(e.target.files[0])}
                  />
                  <p className={`text-xs mt-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-500'
                  }`}>JPEG, PNG, GIF, and WebP formats, up to 2 MB</p>
                  {previewUrlImage2 && (
                    <div className="mt-4">
                      <img src={previewUrlImage2} alt="Preview 2" className="w-full h-64 object-cover rounded" />
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Course Video Provider *</label>
                  <select className={`w-full px-4 py-3 border rounded transition-all duration-200 ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'border-gray-300'
                  }`}>
                    <option>External URL</option>
                    <option>YouTube</option>
                    <option>Vimeo</option>
                    <option>MP4 Upload</option>
                  </select>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Course Video Link *</label>
                  <input
                    type="text"
                    placeholder="External URL Link"
                    value={courseVideoLink}
                    onChange={(e) => setCourseVideoLink(e.target.value)}
                    className={`w-full px-4 py-3 border rounded transition-all duration-200 ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'border-gray-300'
                    }`}
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>Course Thumbnail *</label>
                <input
                  type="file"
                  accept="image/*"
                  className={`border px-4 py-3 rounded w-full transition-all duration-200 ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'border-gray-300'
                  }`}
                  onChange={(e) => setThumbnail(e.target.files[0])}
                />
                <p className={`text-xs mt-2 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-500' : 'text-gray-500'
                }`}>JPEG, PNG, GIF, and WebP formats, up to 2 MB</p>
                {previewUrl && (
                  <div className="mt-4">
                    <img src={previewUrl} alt="Thumbnail Preview" className="w-full h-64 object-cover rounded" />
                  </div>
                )}
              </div>
            </div>
          )}

        {currentStep === 2 && <CurriculumStep courseId={courseId} courseTitle={courseTitle} isDarkMode={isDarkMode} />}
{currentStep === 3 && <AdditionalInformationStep courseId={courseId} courseTitle={courseTitle} isDarkMode={isDarkMode} />}

          {currentStep === 4 && (
            <div className="p-6 bg-white dark:bg-gray-800 rounded-md">
              <h2 className={`text-2xl font-semibold mb-4 flex items-center gap-2 transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-800'
              }`}>Pricing</h2>

              <div className="mb-4">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={isFree}
                    onChange={() => setIsFree(!isFree)}
                    className="form-checkbox text-blue-600 mr-2"
                  />
                  <span className={`transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Check if this is a free course</span>
                </label>
              </div>

              {!isFree && (
                <>
                  <div className="mb-4">
                    <label className={`block font-medium mb-1 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>Course Price (₹)</label>
                    <input
                      type="number"
                      value={coursePrice}
                      onChange={(e) => setCoursePrice(e.target.value)}
                      className={`w-full border rounded px-3 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 ${
                        isDarkMode 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'border-gray-300'
                      }`}
                      placeholder="Enter course price"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={hasDiscount}
                        onChange={() => setHasDiscount(!hasDiscount)}
                        className="form-checkbox text-blue-600 mr-2"
                      />
                      <span className={`transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>Check if this course has discount</span>
                    </label>
                  </div>

                  {hasDiscount && (
                    <div className="mb-2 relative">
                      <label className={`block font-medium mb-1 transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>Discount Percentage (%)</label>
                      <input
                        type="number"
                        value={discountPercentage}
                        onChange={(e) => {
                          const value = Math.max(0, Math.min(100, Number(e.target.value)));
                          setDiscountPercentage(value);
                        }}
                        min="0"
                        max="100"
                        className={`w-full border rounded px-3 py-3 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 ${
                          isDarkMode 
                            ? 'bg-gray-700 border-gray-600 text-white' 
                            : 'border-gray-300'
                        }`}
                        placeholder="Enter discount percentage"
                      />
                      <span className="absolute right-3 top-9 text-gray-500 text-lg font-bold">%</span>
                    </div>
                  )}

                  <p className={`text-sm mb-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    This course has {discountPercentage || 0}% Discount
                  </p>
                  <p className={`text-lg font-semibold mt-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-gray-800'
                  }`}>
                    Total Price: ₹ {totalPrice}
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="mt-6 flex justify-between">
          <button
            onClick={handlePrev}
            disabled={currentStep === 0}
            className={`px-4 py-3 rounded transition-colors duration-200 ${
              currentStep === 0
                ? isDarkMode 
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed' 
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : isDarkMode
                  ? 'bg-gray-700 text-white hover:bg-gray-600'
                  : 'bg-gray-200 hover:bg-gray-300'
            }`}
          >
            Previous
          </button>
          {currentStep < steps.length - 1 ? (
            <button
              onClick={handleNext}
              className="px-4 py-3 bg-blue-900 text-white rounded hover:bg-blue-700 transition-colors duration-200"
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleSubmit}
className={`px-4 py-3 text-white rounded flex items-center gap-2 disabled:opacity-70 transition-colors duration-300 ${
                  isDarkMode
                     ? 'bg-[#1C3664] hover:bg-blue-700 text-white'
              : 'bg-[#1C3664] text-white hover:bg-blue-800'
                }`}
            >
              Submit
            </button>
          )}
        </div>
      </div>

      {/* Add Topic Modal */}
      <TopicModal
        show={showTopicModal}
        onClose={() => setShowTopicModal(false)}
        onSave={handleSaveTopic}
        topicData={newTopicData}
        onChange={setNewTopicData}
        isDarkMode={isDarkMode}
      />

<style jsx>{`
  .quill-dark .ql-toolbar {
    background-color: #374151 !important;
    border-color: #4B5563 !important;
  }
  .quill-dark .ql-toolbar .ql-stroke {
    stroke: #ffffff !important;
  }
  .quill-dark .ql-toolbar .ql-fill {
    fill: #ffffff !important;
  }
  .quill-dark .ql-toolbar .ql-picker-label {
    color: #ffffff !important;
  }
  .quill-dark .ql-toolbar .ql-picker-options {
    background-color: #374151 !important;
    border-color: #4B5563 !important;
  }
  .quill-dark .ql-toolbar .ql-picker-item {
    color: #ffffff !important;
  }
  .quill-dark .ql-container {
    background-color: #1F2937 !important;
    border-color: #4B5563 !important;
    color: #ffffff !important;
  }
  .quill-dark .ql-editor {
    color: #ffffff !important;
    background-color: #1F2937 !important;
  }
  .quill-dark .ql-editor.ql-blank::before {
    color: #9CA3AF !important;
  }
`}</style>
    </div>
  );
};

export default CreateCourse;